import { useEffect, useState, useSyncExternalStore } from "react";
import { CopyPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useOwnerView } from "@/components/LibrarySwitch";
import { fmtBytes } from "@/components/OneDriveQuota";
import { refreshUsage } from "@/components/StoragePill";

let state = { status: "idle" };
const subs = new Set();
let timer = null;
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const emit = (s) => {
  const prev = state;
  state = s;
  subs.forEach((f) => f());
  if (prev.status === "running" && s.status !== "running" && s.job === prev.job) {
    refreshUsage();
    const extra = s.skipped ? `${plural(s.skipped, "song")} already in your library` : undefined;
    if (s.status === "done" && !s.failed) {
      if (s.done) toast.success(`Copied ${plural(s.done, "song")} to your library`, { description: [extra, s.bytes ? fmtBytes(s.bytes) + " used" : null].filter(Boolean).join(" · ") });
      else toast(extra || "Nothing to copy");
    } else toast.error(`${s.failed || "Some"} song${s.failed === 1 ? "" : "s"} couldn't be copied`, { description: (s.errors || [])[0] });
  }
};
const schedule = () => {
  if (state.status === "running" && !timer) timer = setTimeout(() => { timer = null; refreshCopy(); }, 1500);
};
export const refreshCopy = async () => {
  try { const { data } = await api.get("/library/copy"); emit(data); schedule(); } catch { /* ignore */ }
};
export const startCopy = async (body) => {
  const { data } = await api.post("/library/copy", body);
  if (data.status !== "running") {
    toast(data.skipped ? `${plural(data.skipped, "song")} already in your library` : "Nothing to copy");
    state = data;
    subs.forEach((f) => f());
    return data;
  }
  emit(data);
  schedule();
  toast(`Copying ${plural(data.total, "song")} to your library…`, { description: data.skipped ? `${plural(data.skipped, "song")} already there` : undefined });
  return data;
};
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };

export const useCopyStatus = () => {
  const s = useSyncExternalStore(subscribe, () => state);
  useEffect(() => { refreshCopy(); }, []);
  return s;
};

export const useCanCopy = () => {
  const { viewing, canDownload } = useOwnerView();
  return viewing && canDownload;
};

export const copyNow = async (body) => {
  try { await startCopy(body); } catch (e) { toast.error(formatApiError(e)); }
};

export const CopyToLibraryButton = ({ body, label = "Copy to my library", title, size = "md", testid = "copy-to-library" }) => {
  const s = useCopyStatus();
  const [busy, setBusy] = useState(false);
  const running = s.status === "running" || busy;
  const go = async () => {
    if (busy) return;
    setBusy(true);
    await copyNow(body);
    setBusy(false);
  };
  const cls = size === "sm" ? "h-8 px-3 text-xs" : "h-12 px-5 text-sm";
  return (
    <button onClick={go} disabled={running} data-testid={testid} title={title || "Save a copy into your own Resonance storage"}
      className={`flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 transition-colors hover:border-emerald-700 disabled:opacity-50 ${cls}`}>
      {running ? <Loader2 size={size === "sm" ? 12 : 14} className="animate-spin" /> : <CopyPlus size={size === "sm" ? 12 : 14} />}
      {running ? `Copying ${s.done + s.failed}/${s.total}…` : label}
    </button>
  );
};

export const CopyProgress = () => {
  const s = useCopyStatus();
  if (s.status === "idle" || (s.status !== "running" && !s.errors?.length)) return null;
  const pct = s.total ? Math.round(((s.done + s.failed) / s.total) * 100) : 0;
  return (
    <div className="mt-4 rounded-md bg-emerald-50 px-4 py-3 text-xs text-emerald-900" data-testid="copy-progress">
      {s.status === "running" ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin" />Copying to your library · {s.done + s.failed} of {s.total}</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-emerald-100"><div className="h-full rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          {s.current && <p className="mt-1.5 truncate text-emerald-700" data-testid="copy-current">{s.current}</p>}
        </>
      ) : (
        <div data-testid="copy-errors">
          <p className="font-medium text-red-700">{s.failed} song{s.failed === 1 ? "" : "s"} couldn't be copied</p>
          {s.errors.map((e, i) => <p key={i} className="mt-0.5 truncate text-red-700/80">{e}</p>)}
        </div>
      )}
    </div>
  );
};
